import { useState, useRef, useCallback } from 'react';
import axios from 'axios';

// More reliable API URL detection
const getApiUrl = () => {
  // Try different ways to get the API URL
  const envUrl = process.env.REACT_APP_BACKEND_URL || 
                 (typeof window !== 'undefined' && window.process?.env?.REACT_APP_BACKEND_URL);
  
  // Fallback to current origin if env var not available
  const fallbackUrl = typeof window !== 'undefined' ? window.location.origin : '';
  
  const apiUrl = envUrl || fallbackUrl;
  console.log('🔗 API URL resolved to:', apiUrl);
  return apiUrl;
};

const API = getApiUrl();

/**
 * Custom hook for high-quality Text-to-Speech using Emergent Integration with OpenAI
 * Replaces browser Web Speech API with neural-quality voice synthesis
 */
const useEmergenTTS = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const audioRef = useRef(null);

  // Available OpenAI voices with descriptions
  const availableVoices = [
    { id: "nova", name: "Nova", description: "Young female voice, clear and friendly" },
    { id: "alloy", name: "Alloy", description: "Neutral voice, professional" },
    { id: "echo", name: "Echo", description: "Male voice, calm and measured" },
    { id: "fable", name: "Fable", description: "Male voice, warm and expressive" },
    { id: "onyx", name: "Onyx", description: "Male voice, deep and authoritative" },
    { id: "shimmer", name: "Shimmer", description: "Female voice, bright and energetic" }
  ];

  // Default to Nova (young female voice) for task creation
  const defaultVoice = "nova";

  /**
   * Browser TTS fallback using Web Speech API
   */
  const useBrowserTTS = (text, language = 'en') => {
    return new Promise((resolve) => {
      if (!window.speechSynthesis) {
        console.error('❌ Browser TTS: Speech synthesis not supported');
        resolve(false);
        return;
      }

      // Set loading state for browser TTS
      setIsLoading(true);

      // Cancel any ongoing speech
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = language === 'en' ? 'en-US' : 
                       language === 'es' ? 'es-ES' : 
                       language === 'fr' ? 'fr-FR' : 'en-US';
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      utterance.onend = () => {
        console.log('✅ Browser TTS: Playback completed');
        setIsLoading(false); // Make sure loading state is cleared
        setCurrentAudio(null); // Clear audio reference
        resolve(true);
      };

      utterance.onerror = (error) => {
        console.error('❌ Browser TTS: Error:', error);
        setIsLoading(false);
        setCurrentAudio(null);
        resolve(false);
      };

      console.log('🎤 Browser TTS: Speaking with Web Speech API');
      window.speechSynthesis.speak(utterance);
    });
  };

  /**
   * Generate and play high-quality TTS audio
   * @param {string} text - Text to synthesize
   * @param {Object} options - TTS options
   * @param {string} options.voice - Voice ID (nova, alloy, echo, fable, onyx, shimmer)
   * @param {string} options.model - TTS model (tts-1 or tts-1-hd)
   * @param {number} options.speed - Speech speed (0.25 to 4.0)
   * @returns {Promise<boolean>} - Success status
   */
  const speakWithEmergent = useCallback(async (text, options = {}) => {
    if (!text || !text.trim()) {
      console.warn('⚠️ EmergentTTS: Empty text provided');
      return false;
    }

    const {
      voice = defaultVoice,
      model = "tts-1", // Use tts-1 for faster response, tts-1-hd for higher quality
      speed = 1.0,
      response_format = "mp3"
    } = options;

    console.log(`🎤 EmergentTTS: Generating speech for: "${text.substring(0, 50)}..." with voice: ${voice}`);
    console.log(`🔗 EmergentTTS: Using API URL: ${API}`);
    console.log(`🔗 EmergentTTS: Full endpoint URL: ${API}/ai/tts/emergent`);
    
    setIsLoading(true);
    setError(null);

    try {
      // Stop any currently playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }

      // Make request to Emergent TTS endpoint (now with Google TTS fallback)
      const response = await axios.post(`${API}/api/ai/tts/emergent`, {
        text: text,
        voice: voice,
        model: model,
        response_format: response_format,
        speed: speed,
        language: options.language || 'en'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 second timeout for TTS generation
      });

      if (response.data.success && response.data.audio_data) {
        console.log(`✅ EmergentTTS: Generated ${response.data.size} bytes with ${response.data.provider}`);
        console.log(`🔍 EmergentTTS: Audio data starts with:`, response.data.audio_data.substring(0, 50));
        console.log(`🔍 EmergentTTS: Full response:`, {
          success: response.data.success,
          provider: response.data.provider,
          format: response.data.format,
          size: response.data.size,
          has_audio_data: !!response.data.audio_data
        });
        
        // Create audio element from base64 data
        const audio = new Audio(response.data.audio_data);
        console.log('🎤 EmergentTTS: Audio element created');
        setCurrentAudio(audio);
        
        // Store reference for potential cleanup
        if (audioRef.current) {
          audioRef.current = audio;
        }

        // Play the audio
        return new Promise((resolve) => {
          audio.onended = () => {
            console.log('✅ EmergentTTS: Playback completed');
            setIsLoading(false);
            setCurrentAudio(null); // Clear audio reference so isSpeaking becomes false
            resolve(true);
          };

          audio.onerror = (error) => {
            console.error('❌ EmergentTTS: Audio playback error:', error);
            setError('Audio playback failed');
            setIsLoading(false);
            
            // Fallback to browser TTS
            console.log('🔄 Falling back to browser Web Speech API');
            useBrowserTTS(text, options.language || 'en');
            resolve(false);
          };

          audio.onloadstart = () => {
            console.log('🎤 EmergentTTS: Starting audio playback...');
          };

          // Start playback with better error handling
          audio.play().then(() => {
            console.log('✅ EmergentTTS: Audio playback started successfully');
          }).catch(playError => {
            console.error('❌ EmergentTTS: Play failed:', playError);
            console.error('❌ EmergentTTS: Error name:', playError.name);
            console.error('❌ EmergentTTS: Error message:', playError.message);
            
            // Fallback to browser TTS
            console.log('🔄 Falling back to browser Web Speech API');
            setIsLoading(false);
            useBrowserTTS(text, options.language || 'en');
            resolve(true); // Consider it success since we're using fallback
          });
        });

      } else if (response.data.use_browser_fallback) {
        // Backend told us to use browser fallback
        console.log('🔄 Backend unavailable, using browser Web Speech API');
        setIsLoading(false);
        return useBrowserTTS(text, options.language || 'en');
      } else {
        throw new Error('Invalid TTS response format');
      }

    } catch (error) {
      console.error('❌ EmergentTTS: Generation failed:', error);
      
      let errorMessage = 'TTS generation failed';
      if (error.response?.status === 503) {
        errorMessage = 'TTS service temporarily unavailable';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication failed';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'TTS request timed out';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
      setIsLoading(false);
      return false;
    }
  }, [currentAudio]);

  /**
   * Conversational speak function with enhanced features
   * Automatically handles long texts by chunking and provides better error handling
   */
  const speakConversation = useCallback(async (text, voice = defaultVoice, language = 'en') => {
    if (!text) return false;

    // Split long texts into chunks (OpenAI TTS has a limit)
    const maxChunkLength = 4000;
    if (text.length <= maxChunkLength) {
      return await speakWithEmergent(text, { voice, language });
    }

    // Handle long text by splitting at sentence boundaries
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    const chunks = [];
    let currentChunk = '';

    for (const sentence of sentences) {
      if (currentChunk.length + sentence.length <= maxChunkLength) {
        currentChunk += sentence;
      } else {
        if (currentChunk) chunks.push(currentChunk.trim());
        currentChunk = sentence;
      }
    }
    if (currentChunk) chunks.push(currentChunk.trim());

    console.log(`🎤 EmergentTTS: Speaking ${chunks.length} chunks`);

    // Play chunks sequentially
    for (let i = 0; i < chunks.length; i++) {
      const success = await speakWithEmergent(chunks[i], { voice, language });
      if (!success) {
        console.warn(`⚠️ EmergentTTS: Chunk ${i + 1} failed, continuing...`);
        // Continue with remaining chunks even if one fails
      }
      
      // Small pause between chunks
      if (i < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    return true;
  }, [speakWithEmergent]);

  /**
   * Stop any currently playing audio
   */
  const stopSpeaking = useCallback(() => {
    // Stop audio element
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setCurrentAudio(null);
    }
    
    // Stop browser TTS
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
    setIsLoading(false);
    console.log('🔇 EmergentTTS: Audio stopped');
  }, [currentAudio]);

  /**
   * Check if TTS is currently active
   */
  const isSpeaking = isLoading || (currentAudio && !currentAudio.paused);

  return {
    // Main TTS functions
    speakWithEmergent,
    speakConversation,
    stopSpeaking,
    
    // State
    isLoading,
    isSpeaking,
    error,
    
    // Voice options
    availableVoices,
    defaultVoice,
    
    // Audio reference for advanced usage
    audioRef,
    currentAudio
  };
};

export default useEmergenTTS;